import { supabase } from '@/lib/supabase';

/**
 * Tüm aktif gayrimenkulleri getir
 * @param {Object} filters - Filtreleme seçenekleri
 * @param {string} filters.type - villa | apartman | arsa | ticari
 * @param {string} filters.status - satilik | kiralik
 * @param {number} filters.minPrice - Minimum fiyat (GBP)
 * @param {number} filters.maxPrice - Maximum fiyat (GBP)
 * @param {number} filters.bedrooms - Minimum yatak odası sayısı
 * @param {boolean} filters.featured - Sadece öne çıkan ilanlar
 * @param {string} filters.projectStatus - planning | under_construction | completed | ready_to_move
 * @param {boolean} filters.citizenshipEligible - Vatandaşlık uygun
 * @param {boolean} filters.rentalGuaranteed - Garantili kira
 * @returns {Promise<Array>} Gayrimenkul listesi
 */
export async function getProperties(filters = {}) {
  try {
    let query = supabase
      .from('properties')
      .select(`
        *,
        property_images(
          id,
          image_url,
          display_order,
          alt_text_tr,
          alt_text_en,
          is_primary
        )
      `)
      .eq('is_active', true)
      .order('priority_listing', { ascending: false })
      .order('created_at', { ascending: false });

    // Filtreler
    if (filters.type) {
      query = query.eq('type', filters.type);
    }
    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    if (filters.minPrice) {
      query = query.gte('price_gbp', filters.minPrice);
    }
    if (filters.maxPrice) {
      query = query.lte('price_gbp', filters.maxPrice);
    }
    if (filters.bedrooms) {
      query = query.gte('bedrooms', filters.bedrooms);
    }
    if (filters.featured) {
      query = query.eq('featured', true);
    }
    if (filters.projectStatus) {
      query = query.eq('project_status', filters.projectStatus);
    }
    if (filters.citizenshipEligible) {
      query = query.eq('citizenship_eligible', true);
    }
    if (filters.rentalGuaranteed) {
      query = query.eq('rental_guaranteed', true);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Görselleri display_order'a göre sırala
    const processedData = data?.map(property => ({
      ...property,
      property_images: property.property_images?.sort((a, b) => a.display_order - b.display_order) || []
    }));

    return processedData || [];
  } catch (error) {
    console.error('Error fetching properties:', error);
    throw error;
  }
}

/**
 * Tek bir gayrimenkul getir (ID ile)
 * @param {string} id - Property UUID
 * @returns {Promise<Object>} Gayrimenkul detayı
 */
export async function getPropertyById(id) {
  try {
    const { data, error } = await supabase
      .from('properties')
      .select(`
        *,
        property_images(
          id,
          image_url,
          display_order,
          alt_text_tr,
          alt_text_en,
          is_primary
        )
      `)
      .eq('id', id)
      .eq('is_active', true)
      .single();

    if (error) throw error;

    // Görselleri display_order'a göre sırala
    if (data && data.property_images) {
      data.property_images.sort((a, b) => a.display_order - b.display_order);
    }

    // Görüntülenme sayısını artır
    incrementViews(id).catch(() => {});

    return data;
  } catch (error) {
    console.error('Error fetching property by id:', error);
    throw error;
  }
}

/**
 * Slug ile gayrimenkul getir (SEO-friendly URL)
 * @param {string} slug - Property slug
 * @returns {Promise<Object>} Gayrimenkul detayı
 */
export async function getPropertyBySlug(slug) {
  try {
    const { data, error } = await supabase
      .from('properties')
      .select(`
        *,
        property_images(
          id,
          image_url,
          display_order,
          alt_text_tr,
          alt_text_en,
          is_primary
        )
      `)
      .eq('slug', slug)
      .eq('is_active', true)
      .single();

    if (error) throw error;

    // Görselleri display_order'a göre sırala
    if (data && data.property_images) {
      data.property_images.sort((a, b) => a.display_order - b.display_order);
    }

    // Görüntülenme sayısını artır
    if (data?.id) {
      incrementViews(data.id).catch(() => {});
    }

    return data;
  } catch (error) {
    console.error('Error fetching property by slug:', error);
    throw error;
  }
}

/**
 * Öne çıkan ilanları getir
 * @returns {Promise<Array>} Öne çıkan gayrimenkul listesi
 */
export async function getFeaturedProperties() {
  try {
    const { data, error } = await supabase
      .from('properties')
      .select(`
        *,
        property_images(
          id,
          image_url,
          display_order,
          alt_text_tr,
          alt_text_en,
          is_primary
        )
      `)
      .eq('is_active', true)
      .eq('featured', true)
      .order('priority_listing', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) throw error;

    const processedData = data?.map(property => ({
      ...property,
      property_images: property.property_images?.sort((a, b) => a.display_order - b.display_order) || []
    }));

    return processedData || [];
  } catch (error) {
    console.error('Error fetching featured properties:', error);
    throw error;
  }
}

/**
 * Vatandaşlık için uygun ilanları getir
 * @returns {Promise<Array>} Vatandaşlık uygun gayrimenkul listesi
 */
export async function getCitizenshipProperties() {
  try {
    const { data, error } = await supabase
      .from('properties')
      .select(`
        *,
        property_images(
          id,
          image_url,
          display_order,
          alt_text_tr,
          alt_text_en,
          is_primary
        )
      `)
      .eq('is_active', true)
      .eq('citizenship_eligible', true)
      .order('priority_listing', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) throw error;

    const processedData = data?.map(property => ({
      ...property,
      property_images: property.property_images?.sort((a, b) => a.display_order - b.display_order) || []
    }));

    return processedData || [];
  } catch (error) {
    console.error('Error fetching citizenship properties:', error);
    throw error;
  }
}

/**
 * Garantili kiralıklı ilanları getir
 * @returns {Promise<Array>} Garantili kira gayrimenkul listesi
 */
export async function getRentalGuaranteedProperties() {
  try {
    const { data, error } = await supabase
      .from('properties')
      .select(`
        *,
        property_images(
          id,
          image_url,
          display_order,
          alt_text_tr,
          alt_text_en,
          is_primary
        )
      `)
      .eq('is_active', true)
      .eq('rental_guaranteed', true)
      .order('rental_yield', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) throw error;

    const processedData = data?.map(property => ({
      ...property,
      property_images: property.property_images?.sort((a, b) => a.display_order - b.display_order) || []
    }));

    return processedData || [];
  } catch (error) {
    console.error('Error fetching rental guaranteed properties:', error);
    throw error;
  }
}

/**
 * Supabase'den gelen property verisini mevcut bileşenlerin beklediği formata dönüştürür
 * @param {Object} sp - Supabase'den gelen ham property verisi
 * @param {string} lang - Dil kodu (tr, en, ar, ru)
 * @returns {Object|null} Dönüştürülmüş property objesi
 */
export function transformProperty(sp, lang = 'en') {
  if (!sp) return null;

  const get = (field) => sp[`${field}_${lang}`] || sp[`${field}_en`] || '';

  return {
    id: sp.id,
    slug: sp.slug || sp.id,
    title: get('title'),
    location: get('location'),
    description: get('description'),
    priceGBP: sp.price_gbp,
    sqm: sp.sqm,
    type: sp.type,
    status: sp.status,
    bedrooms: sp.bedrooms || 0,
    bathrooms: sp.bathrooms || 0,
    floors: sp.floors || 0,
    yearBuilt: sp.year_built || 0,
    distanceSea: sp.distance_sea || '',
    titleType: sp.title_type || '',
    parking: !!sp.parking,
    pool: !!sp.pool,
    garden: !!sp.garden,
    rental: !!sp.rental,
    featured: !!sp.featured,
    lat: sp.latitude,
    lng: sp.longitude,
    images: sp.property_images?.map(img => img.image_url) || [],
    image: sp.property_images?.[0]?.image_url || '',
    // Yatırım alanları
    expectedROI: sp.expected_roi || null,
    rentalYield: sp.rental_yield || null,
    rentalIncomeMonthly: sp.rental_income_monthly || null,
    rentalGuaranteed: !!sp.rental_guaranteed,
    rentalGuaranteeYears: sp.rental_guarantee_years || null,
    capitalAppreciationPotential: sp.capital_appreciation_potential || null,
    // Proje bilgileri
    projectStatus: sp.project_status || null,
    projectName: get('project_name'),
    developerName: sp.developer_name || null,
    completionDate: sp.completion_date || null,
    totalUnits: sp.total_units || null,
    availableUnits: sp.available_units || null,
    // Finansal
    downPaymentPercentage: sp.down_payment_percentage || null,
    installmentMonths: sp.installment_months || null,
    financingAvailable: !!sp.financing_available,
    paymentPlan: get('payment_plan'),
    // Vatandaşlık
    citizenshipEligible: !!sp.citizenship_eligible,
    citizenshipInvestmentThreshold: sp.citizenship_investment_threshold || null,
    legalStatus: sp.legal_status || null,
    // Medya
    videoUrl: sp.video_url || null,
    virtualTourUrl: sp.virtual_tour_url || null,
    tour360Url: sp.tour_360_url || null,
    brochureUrl: sp.brochure_url || null,
    floorPlanUrl: sp.floor_plan_url || null,
    // Ek özellikler
    furnished: !!sp.furnished,
    seaView: !!sp.sea_view,
    mountainView: !!sp.mountain_view,
    cityView: !!sp.city_view,
    balcony: !!sp.balcony,
    terrace: !!sp.terrace,
    elevator: !!sp.elevator,
    security247: !!sp.security_24_7,
    gym: !!sp.gym,
    spa: !!sp.spa,
    playground: !!sp.playground,
    concierge: !!sp.concierge,
    // Enerji
    energyRating: sp.energy_rating || null,
    solarPanels: !!sp.solar_panels,
    smartHome: !!sp.smart_home,
    // SEO
    metaTitle: get('meta_title'),
    metaDescription: get('meta_description'),
    metaKeywords: get('meta_keywords'),
    // İstatistikler
    viewsCount: sp.views_count || 0,
    contactCount: sp.contact_count || 0,
    priorityListing: sp.priority_listing || 0,
  };
}

/**
 * Yeni gayrimenkul ekle
 * @param {Object} propertyData - Gayrimenkul verileri
 * @param {Array} images - Görsel dosyaları
 * @returns {Promise<Object>} Eklenen gayrimenkul
 */
export async function createProperty(propertyData, images = []) {
  try {
    // 1. Property kaydını oluştur
    const { data: property, error: propertyError } = await supabase
      .from('properties')
      .insert([propertyData])
      .select()
      .single();

    if (propertyError) throw propertyError;

    // 2. Eğer görseller varsa, onları da ekle
    if (images && images.length > 0 && property) {
      const imageRecords = images.map((img, index) => ({
        property_id: property.id,
        image_url: img.url,
        display_order: index,
        alt_text_tr: img.alt_text_tr || '',
        alt_text_en: img.alt_text_en || '',
        is_primary: index === 0,
      }));

      const { error: imagesError } = await supabase
        .from('property_images')
        .insert(imageRecords);

      if (imagesError) {
        console.error('Error inserting images:', imagesError);
      }
    }

    return property;
  } catch (error) {
    console.error('Error creating property:', error);
    throw error;
  }
}

/**
 * Gayrimenkul güncelle
 * @param {string} id - Property UUID
 * @param {Object} propertyData - Güncellenecek veriler
 * @returns {Promise<Object>} Güncellenen gayrimenkul
 */
export async function updateProperty(id, propertyData) {
  try {
    const { data, error } = await supabase
      .from('properties')
      .update(propertyData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating property:', error);
    throw error;
  }
}

/**
 * Gayrimenkul sil (soft delete)
 * @param {string} id - Property UUID
 * @returns {Promise<void>}
 */
export async function deleteProperty(id) {
  try {
    const { error } = await supabase
      .from('properties')
      .update({ is_active: false })
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting property:', error);
    throw error;
  }
}

/**
 * Tüm gayrimenkulleri getir (admin, aktif olmayanlar dahil)
 * @returns {Promise<Array>} Tüm gayrimenkul listesi
 */
export async function getAllProperties() {
  try {
    const { data, error } = await supabase
      .from('properties')
      .select(`
        id,
        title_tr,
        title_en,
        price_gbp,
        type,
        status,
        is_active,
        featured,
        slug,
        project_status,
        expected_roi,
        rental_yield,
        views_count,
        contact_count,
        created_at,
        property_images(
          id,
          image_url,
          is_primary
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching all properties:', error);
    throw error;
  }
}

/**
 * Dosyayı Supabase Storage'a yükle
 * @param {File} file - Yüklenecek dosya
 * @param {string} bucket - Bucket adı
 * @returns {Promise<string>} Public URL
 */
export async function uploadImage(file, bucket = 'property-images') {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    // Public URL al
    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath);

    return publicUrl;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
}

/**
 * Görüntülenme sayısını artır (RPC)
 * @param {string} propertyId - Property UUID
 */
export async function incrementViews(propertyId) {
  try {
    await supabase.rpc('increment_property_views', { property_id: propertyId });
  } catch (error) {
    // Sessizce devam et - kritik değil
    console.warn('Could not increment views:', error.message);
  }
}

/**
 * İletişim sayısını artır (RPC)
 * @param {string} propertyId - Property UUID
 */
export async function incrementContacts(propertyId) {
  try {
    await supabase.rpc('increment_property_contacts', { property_id: propertyId });
  } catch (error) {
    console.warn('Could not increment contacts:', error.message);
  }
}

/**
 * Yatırım istatistiklerini getir
 * @returns {Promise<Object>} İstatistikler
 */
export async function getInvestmentStats() {
  try {
    const { data, error } = await supabase
      .from('properties')
      .select('expected_roi, rental_yield, price_gbp, citizenship_eligible')
      .eq('is_active', true);

    if (error) throw error;

    const stats = {
      totalProperties: data.length,
      avgROI: 0,
      avgRentalYield: 0,
      citizenshipCount: 0,
      minPrice: 0,
      maxPrice: 0,
    };

    if (data.length > 0) {
      const roiValues = data.filter(p => p.expected_roi).map(p => p.expected_roi);
      const yieldValues = data.filter(p => p.rental_yield).map(p => p.rental_yield);
      const prices = data.map(p => p.price_gbp);

      stats.avgROI = roiValues.length > 0
        ? roiValues.reduce((a, b) => a + b, 0) / roiValues.length
        : 0;
      stats.avgRentalYield = yieldValues.length > 0
        ? yieldValues.reduce((a, b) => a + b, 0) / yieldValues.length
        : 0;
      stats.citizenshipCount = data.filter(p => p.citizenship_eligible).length;
      stats.minPrice = Math.min(...prices);
      stats.maxPrice = Math.max(...prices);
    }

    return stats;
  } catch (error) {
    console.error('Error fetching investment stats:', error);
    throw error;
  }
}
